/**
 * Meta line of one row in the sidebar database overview:
 * column count and how many automations read from / write into it.
 */
import { useI18n } from '@affine/i18n';
import { type FC, Fragment } from 'react';

import type { AutomationInfo } from '../services/automation-discovery';
import * as styles from './workspace-overview.css';

export interface DatabaseAutomationUsage {
  input: number;
  output: number;
}

/** Counts input/output references per database block ID. */
export function countDatabaseAutomationUsage(
  automations: readonly Pick<
    AutomationInfo,
    'inputTableIds' | 'outputTableIds'
  >[]
): ReadonlyMap<string, DatabaseAutomationUsage> {
  const usage = new Map<string, DatabaseAutomationUsage>();
  const entry = (databaseId: string) => {
    let current = usage.get(databaseId);
    if (!current) {
      current = { input: 0, output: 0 };
      usage.set(databaseId, current);
    }
    return current;
  };
  for (const automation of automations) {
    for (const databaseId of new Set(automation.inputTableIds)) {
      entry(databaseId).input += 1;
    }
    for (const databaseId of new Set(automation.outputTableIds)) {
      entry(databaseId).output += 1;
    }
  }
  return usage;
}

export const DatabaseOverviewMeta: FC<{
  columnCount: number;
  usage: DatabaseAutomationUsage | undefined;
}> = ({ columnCount, usage }) => {
  const t = useI18n();
  const segments = [
    t['com.streamlain.streamlain.workspace.columns-count']({
      count: String(columnCount),
    }),
  ];
  if (usage?.input) {
    segments.push(
      usage.input === 1
        ? t['com.streamlain.streamlain.workspace.database-input-for-one']()
        : t['com.streamlain.streamlain.workspace.database-input-for']({
            count: String(usage.input),
          })
    );
  }
  if (usage?.output) {
    segments.push(
      usage.output === 1
        ? t['com.streamlain.streamlain.workspace.database-output-for-one']()
        : t['com.streamlain.streamlain.workspace.database-output-for']({
            count: String(usage.output),
          })
    );
  }

  return (
    <div className={styles.itemMeta}>
      {segments.map((segment, index) => (
        <Fragment key={segment}>
          {index > 0 && (
            <span className={styles.metaSeparator} aria-hidden="true">
              ·
            </span>
          )}
          <span className={styles.metaSegment}>{segment}</span>
        </Fragment>
      ))}
    </div>
  );
};
